import { CSS_CLASSES } from "../../constants/app";
import { FillQuestionnaireIcon } from "../icons/questionnaireIcons";
import { HistoryListSkeleton } from "../loading/LoadingIndicators";
import PatientDetailModal from "../PatientDetailModal";
import type { QuestionnaireHistoryItem } from "./questionnaire.types";

interface QuestionnaireHistoryViewProps {
  history: QuestionnaireHistoryItem[];
  historyLoading: boolean;
  historyError: string | null;
  selectedSubmission: QuestionnaireHistoryItem | null;
  setSelectedSubmission: (value: QuestionnaireHistoryItem | null) => void;
  onAddQuestionnaire: () => void;
  addButtonClassName: string;
  questionLabelMap: Map<string, string>;
  questionnaireSectionTitleMap: Map<string, string>;
  formatAnswerValue: (
    questionId: string,
    answer: { value?: string; data?: Array<{ value: string }> }
  ) => string;
  formatTimelineDateTime: (dateString: string) => string;
}

export default function QuestionnaireHistoryView({
  history,
  historyLoading,
  historyError,
  selectedSubmission,
  setSelectedSubmission,
  onAddQuestionnaire,
  addButtonClassName,
  questionLabelMap,
  questionnaireSectionTitleMap,
  formatAnswerValue,
  formatTimelineDateTime
}: QuestionnaireHistoryViewProps) {
  const renderBodyContent = () => {
    if (historyLoading) {
      return <HistoryListSkeleton />;
    }

    if (historyError) {
      return <div className={CSS_CLASSES.TEXT.ERROR}>{historyError}</div>;
    }

    if (history.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-guardian-20 bg-guardian-10 p-6">
          <p className="text-sm font-medium text-guardian-body">No questionnaire added yet.</p>
          <p className={`mt-1 ${CSS_CLASSES.TEXT.MUTED}`}>
            Click Fill Questionnaire to create the first submission.
          </p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-guardian-10">
        {history.map((entry) => (
          <button
            key={entry._id}
            type="button"
            onClick={() => setSelectedSubmission(entry)}
            className="group flex w-full flex-col gap-2 py-3 text-left transition-colors hover:bg-guardian-10/60 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          >
            <div className="min-w-0 flex-1">
              <p className="break-words text-sm font-semibold text-guardian-body">
                {questionnaireSectionTitleMap.get(entry.questionnaireId) ||
                  entry.questionnaireId}
              </p>
              <p className="mt-1 text-[12px] leading-4 tracking-[0.4px] text-guardian-header">
                {formatTimelineDateTime(entry.created_at)}
              </p>
            </div>
            <span className="shrink-0 text-sm font-semibold text-rag-green group-hover:underline">
              View details
            </span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-4 flex shrink-0 flex-col gap-3 p-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-base font-bold tracking-[-0.02em] text-guardian-body">
            Questionnaire History
          </h3>
          <p className={`mt-0.5 ${CSS_CLASSES.TEXT.MUTED}`}>
            View previously submitted questionnaire entries.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddQuestionnaire}
          className={`shrink-0 gap-1.5 ${addButtonClassName}`}
        >
          <FillQuestionnaireIcon className="h-5 w-5 shrink-0 text-white" />
          <span>Fill Questionnaire</span>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">{renderBodyContent()}</div>

      <PatientDetailModal
        isOpen={!!selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
        title="Submission Details"
        subtitle={
          selectedSubmission
            ? formatTimelineDateTime(selectedSubmission.created_at)
            : undefined
        }
        size="lg"
      >
        <div className="space-y-3 p-4 sm:p-6">
          {selectedSubmission?.answers?.length ? (
            selectedSubmission.answers.map((answer, index) => (
              <div
                key={`${answer.questionId}-${index}`}
                className="rounded-lg border border-guardian-20 p-3"
              >
                <p className="text-sm font-medium text-guardian-body">
                  {questionLabelMap.get(answer.questionId) || answer.questionId}
                </p>
                <p className={`mt-1 ${CSS_CLASSES.TEXT.MUTED}`}>
                  {formatAnswerValue(answer.questionId, answer)}
                </p>
              </div>
            ))
          ) : (
            <p className={CSS_CLASSES.TEXT.MUTED}>No answers available.</p>
          )}
        </div>
      </PatientDetailModal>
    </div>
  );
}
